import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'astroturf/react'

import { dismissValidationError } from 'actions/errors'
import { eventTypeSelector } from 'root-api'
import {
  inviteeEmailErrorsSelector,
  inviteeFirstNameErrorsSelector,
  inviteeFullNameErrorsSelector,
  inviteeLastNameErrorsSelector
} from 'selectors'
import BookingField from 'components/enter_details/booking_field'
import { EnterDetailsFieldset } from 'components/enter_details/form_field_spacing'
import BookingTextField from 'components/form/text'
import { useI18n } from 'locales'
import _Row from "invitee_inputs-Row.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/invitee_inputs.js?styleId=Row";
import _Column from "invitee_inputs-Column.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/invitee_inputs.js?styleId=Column";


const FirstName = ({ firstName: { value, required }, onChange }) => {
  const { i18n } = useI18n()
  const firstNameErrors = useSelector(inviteeFirstNameErrorsSelector)
  const dispatch = useDispatch()
  const dismissError = () => dispatch(dismissValidationError('invitee', 'first_name'))

  const handleChange = (...args) => {
    dismissError()
    onChange(...args)
  }

  return (
    <BookingField>
      <BookingTextField
        label={i18n.t('booking.templates.first_name')}
        name="first_name"
        value={value}
        maxLength={155}
        required={required}
        onChange={handleChange}
        error={firstNameErrors?.[0]}
        autoComplete="given-name"
      />
    </BookingField>
  )
}

const LastName = ({ lastName: { value, required }, onChange }) => {
  const { i18n } = useI18n()
  const lastNameErrors = useSelector(inviteeLastNameErrorsSelector)
  const dispatch = useDispatch()
  const dismissError = () => dispatch(dismissValidationError('invitee', 'last_name'))

  const handleChange = (...args) => {
    dismissError()
    onChange(...args)
  }

  return (
    <BookingField>
      <BookingTextField
        label={i18n.t('booking.templates.last_name')}
        name="last_name"
        value={value}
        maxLength={155}
        required={required}
        onChange={handleChange}
        error={lastNameErrors?.[0]}
        autoComplete="family-name"
      />
    </BookingField>
  )
}

const Row = /*#__PURE__*/styled('div', null, {
  displayName: "Row",
  styles: _Row
})

const Column = /*#__PURE__*/styled('div', null, {
  displayName: "Column",
  styles: _Column
})

const SeparatedInput = ({ firstName, lastName, onChange }) => (
  <Row>
    <Column data-component="invitee-input-first-name">
      <FirstName firstName={firstName} onChange={onChange} />
    </Column>
    <Column data-component="invitee-input-last-name">
      <LastName lastName={lastName} onChange={onChange} />
    </Column>
  </Row>
)

const CombinedInput = ({ fullName: { value, required }, onChange }) => {
  const { i18n } = useI18n()
  const fullNameErrors = useSelector(inviteeFullNameErrorsSelector)
  const dispatch = useDispatch()
  const dismissError = () => dispatch(dismissValidationError('invitee', 'full_name'))

  const handleChange = (...args) => {
    dismissError()
    onChange(...args)
  }

  return (
    <BookingField>
      <BookingTextField
        id="full_name_input"
        label={i18n.t('booking.templates.name')}
        name="full_name"
        value={value}
        maxLength={155}
        required={required}
        onChange={handleChange}
        error={fullNameErrors?.[0]}
        autoComplete="name"
      />
    </BookingField>
  )
}

export const EmailInput = ({ email: { value, required }, onChange }) => {
  const { i18n } = useI18n()
  const emailErrors = useSelector(inviteeEmailErrorsSelector)
  const dispatch = useDispatch()
  const dismissError = () => dispatch(dismissValidationError('invitee', 'email'))

  const handleChange = (...args) => {
    dismissError()
    onChange(...args)
  }

  return (
    <BookingField>
      <BookingTextField
        id="email_input"
        label={i18n.t('booking.templates.email')}
        type="email"
        name="email"
        value={value}
        required={required}
        onChange={handleChange}
        error={emailErrors?.[0]}
        autoComplete="email"
      />
    </BookingField>
  )
}

const InviteeInputs = ({
  fullName = null,
  firstName = null,
  lastName = null,
  email = null,
  onChange
}) => {
  const { invitee_name_format: nameFormat } = useSelector(eventTypeSelector)
  return (
    <EnterDetailsFieldset>
      {nameFormat === 'separated' && <SeparatedInput firstName={firstName} lastName={lastName} onChange={onChange} />}
      {nameFormat === 'combined' && <CombinedInput fullName={fullName} onChange={onChange} />}
      <EmailInput email={email} onChange={onChange} />
    </EnterDetailsFieldset>
  )
}

InviteeInputs.propTypes = {
  onChange: PropTypes.func.isRequired,
  fullName: PropTypes.shape({
    value: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired
  }),
  firstName: PropTypes.shape({
    value: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired
  }),
  lastName: PropTypes.shape({
    value: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired
  }),
  email: PropTypes.shape({
    value: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired
  })
}

export default InviteeInputs
